import { useSelector } from 'react-redux';

import { CoreStatusExtended, EventBookingStatus, SectionStatusType } from 'types';

import { SectionId } from '../components/molecules/Sidebar/types';
import { ApplicationState } from '../store';
import { StudentState } from '../store/student/types';

export const LockChecklistStatuses = [
  CoreStatusExtended.CAS_ISSUED,
  CoreStatusExtended.PATHWAY_REVIEW,
  CoreStatusExtended.VISA_ISSUED,
  CoreStatusExtended.READY_FOR_CAS,
  CoreStatusExtended.VISA_REFUSED
];

export const useIsSectionLocked = (section: SectionId, status?: CoreStatusExtended) => {
  const student = useSelector((state: ApplicationState) => state.student);
  const config = useSelector((state: ApplicationState) => state.config);
  if (!status) return false;

  const isChecklistComplete = calcIsChecklistComplete(student, config);

  const isChecklistLocked = isChecklistComplete && LockChecklistStatuses.includes(status);

  const isDocSectionComplete = student.summary.docsComplete === student.summary.docsRequired;

  const isGeneralDocLocked =
    isDocSectionComplete && [CoreStatusExtended.VISA_ISSUED, CoreStatusExtended.VISA_REFUSED].includes(status);

  const isFinancialDocComplete = student.summary.finDocsRequired === student.summary.finDocsComplete;
  const isFinancialDocLocked =
    isFinancialDocComplete && [CoreStatusExtended.VISA_ISSUED, CoreStatusExtended.VISA_REFUSED].includes(status);

  const calendlyInterviews = student.offer.calendlyInterviews ?? [];
  const isScheduledInterviewsComplete = calendlyInterviews.every(interview => {
    return Object.entries(interview.scheduledEvents ?? {}).every(([key, event]) => {
      return [EventBookingStatus.BOOKED, EventBookingStatus.CANCELED].includes(event.bookingStatus);
    });
  });

  const isScheduledInterviewLocked =
    [CoreStatusExtended.CAS_ISSUED, CoreStatusExtended.VISA_ISSUED, CoreStatusExtended.VISA_REFUSED].includes(status) &&
    isScheduledInterviewsComplete;

  const lockedSections: { [key in SectionId]?: boolean } = {
    [SectionId.CHECKLIST]: isChecklistLocked,
    [SectionId.GENERAL_DOC]: isGeneralDocLocked,
    [SectionId.FINANCE_DOC]: isFinancialDocLocked,
    [SectionId.SCHEDULED_INTERVIEWS]: isScheduledInterviewLocked
  };
  return lockedSections[section];
};

export const calcIsChecklistComplete = (student: StudentState, config: any) => {
  const historyInactive = config.historySectionStatus === SectionStatusType.INACTIVE;
  const financeInactive = config.financeSectionStatus === SectionStatusType.INACTIVE;

  const isComplete =
    (student.offer.isPISectionComplete || !student.summary.studentDetailsRequired) &&
    (student.offer.isHSectionComplete || !student.summary.historyRequired || historyInactive) &&
    (student.consents.isSectionComplete || !student.summary.consentsRequired) &&
    (student.financials.isSectionComplete || !student.summary.financeRequired || financeInactive);

  return isComplete;
};
